import { useAuth0 } from '@auth0/auth0-react';
import { type ErrorComponentProps, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { isReauthRequired } from '../auth/reauth-required';
import { PrimaryButton } from './buttons';

// Shown briefly while loginWithRedirect() navigates to Auth0.
// Preserves the current path so the user is returned here after login.
function AuthRequiredRedirect() {
  const { loginWithRedirect } = useAuth0();
  const router = useRouter();

  useEffect(() => {
    loginWithRedirect({
      appState: { returnTo: router.state.location.pathname },
    });
  }, [loginWithRedirect, router]);

  return (
    <main className="grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8">
      <div className="text-center">
        <p className="text-base font-semibold text-gray-600">Session expired</p>
        <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          Redirecting to login&hellip;
        </h1>
      </div>
    </main>
  );
}

/**
 * React raises a DOMException from its commit phase when its fiber tree and the
 * live DOM have diverged — it removes or inserts a node that is no longer where
 * it recorded it. Re-rendering cannot repair that: `router.invalidate()` mounts
 * fresh nodes into the same damaged DOM and fails the same way, so a full
 * document reload is the only recovery. Offer that instead of "Retry".
 */
function isDomDesyncError(error: Error): boolean {
  return (
    error instanceof DOMException &&
    (error.name === 'NotFoundError' || error.name === 'HierarchyRequestError')
  );
}

function DomDesyncError() {
  return (
    <div className="grid min-h-full place-items-center px-6 py-24">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900">This page stopped displaying correctly</h1>
        <p className="mt-4 text-base text-gray-600">
          Something went wrong while the page was updating. Your data is safe; only what's on screen
          is affected, and reloading rebuilds it.
        </p>
        <div className="mt-6 flex items-center justify-center gap-x-6">
          <PrimaryButton onClick={() => window.location.reload()}>Reload the page</PrimaryButton>
          <a href="mailto:hello@liquorstore.tech" className="text-sm font-semibold text-gray-900">
            Tell us if it keeps happening <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </div>
    </div>
  );
}

export function FullPageError({ error }: ErrorComponentProps) {
  const router = useRouter();

  if (isReauthRequired(error)) {
    return <AuthRequiredRedirect />;
  }

  if (isDomDesyncError(error)) {
    return <DomDesyncError />;
  }

  return (
    <main className="grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8">
      <div className="text-center">
        <p className="text-base font-semibold text-red-600">Error</p>
        <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          Something went wrong
        </h1>
        <p className="mt-6 text-base leading-7 text-gray-600">
          {error.message || 'An unexpected error occurred.'}
        </p>
        <div className="mt-10 flex items-center justify-center gap-x-6">
          <PrimaryButton onClick={() => router.invalidate()}>Try again</PrimaryButton>
          <a href="mailto:hello@liquorstore.tech" className="text-sm font-semibold text-gray-900">
            Contact support <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </div>
    </main>
  );
}

export function AuthError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <main className="grid min-h-full place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8">
      <div className="text-center">
        <p className="text-base font-semibold text-red-600">Authentication Error</p>
        <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl">
          Login was not completed
        </h1>
        <p className="mt-6 text-base leading-7 text-gray-600">{message}</p>
        <div className="mt-10 flex items-center justify-center gap-x-6">
          <PrimaryButton onClick={onRetry}>Try again</PrimaryButton>
          <a href="mailto:hello@liquorstore.tech" className="text-sm font-semibold text-gray-900">
            Contact support <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </div>
    </main>
  );
}

function ContentError({
  title,
  error,
  defaultMessage,
}: {
  title: string;
  error: Error;
  defaultMessage: string;
}) {
  const router = useRouter();

  if (isReauthRequired(error)) {
    return <AuthRequiredRedirect />;
  }

  if (isDomDesyncError(error)) {
    return <DomDesyncError />;
  }

  return (
    <div className="grid min-h-full place-items-center px-6 py-24">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <p className="mt-4 text-base text-gray-600">{error.message || defaultMessage}</p>
        <div className="mt-6">
          <PrimaryButton onClick={() => router.invalidate()}>Retry</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export function StoreError({ error }: ErrorComponentProps) {
  return (
    <ContentError
      title="Couldn't load store"
      error={error}
      defaultMessage="There was a problem loading this store."
    />
  );
}

export function RouteError({ error }: ErrorComponentProps) {
  return (
    <ContentError
      title="Couldn't load this page"
      error={error}
      defaultMessage="There was a problem loading this page."
    />
  );
}
